import React, { useRef, useEffect, useState } from 'react';
import logo from '../logo.png';

function ProposalPage({ entry, projectNumber, onBack }) {
  const canvasRef = useRef(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [sendStatus, setSendStatus] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const stallImg = new Image();
    stallImg.crossOrigin = 'anonymous';
    stallImg.src = entry.stall.images?.[0] || '';
    stallImg.onload = () => {
      ctx.drawImage(stallImg, 0, 0, canvas.width, canvas.height);
      if (!entry.logos?.length) return;
      entry.logos.forEach((logoItem) => {
        const img = new Image();
        img.src = logoItem.src;
        img.onload = () => {
          const x = (logoItem.pos.x / 100) * canvas.width;
          const y = (logoItem.pos.y / 100) * canvas.height;
          ctx.drawImage(img, x, y, logoItem.size, logoItem.size);
        };
      });
    };
    stallImg.onerror = () => {
      ctx.fillStyle = entry.stall.color;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = '64px serif';
      ctx.textAlign = 'center';
      ctx.fillText(entry.stall.icon, canvas.width / 2, canvas.height / 2 + 20);
    };
  }, [entry]);

  const handlePrint = () => window.print();

  const handleSendProposal = async () => {
    if (!clientEmail || !clientEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }
    setSendStatus('sending');
    const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
    const eventDateFormatted = entry.eventDateFrom
      ? `${fmt(entry.eventDateFrom)}${entry.eventDateTo ? ' — ' + fmt(entry.eventDateTo) : ''}`
      : (entry.eventDate ? fmt(entry.eventDate) : '—');
    const savedOnFormatted = new Date(entry.savedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

    const payload = {
      projectNumber,
      clientName: entry.clientName || '—',
      clientEmail,
      exhibition: entry.exhibition || '—',
      location: entry.location || '—',
      eventDate: eventDateFormatted,
      savedOn: savedOnFormatted,
      stall: entry.stall,
      logos: entry.logos || [],
    };

    try {
      const res = await fetch('/send-proposal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setSendStatus('sent');
        setTimeout(() => {
          setSendStatus(null);
          setShowEmailModal(false);
          setClientEmail('');
        }, 3000);
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      console.error(err);
      setSendStatus('error');
      setTimeout(() => setSendStatus(null), 4000);
    }
  };

  const _fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
  const eventDateFormatted = entry.eventDateFrom
    ? `${_fmt(entry.eventDateFrom)}${entry.eventDateTo ? ' — ' + _fmt(entry.eventDateTo) : ''}`
    : (entry.eventDate ? _fmt(entry.eventDate) : '—');
  const savedOnFormatted = new Date(entry.savedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ minHeight: '100vh', background: '#F7F5F2' }}>
      {/* Header - hide on print */}
      <header className="no-print" style={{ background: '#fff', borderBottom: '1px solid #ede9e3', padding: '0 2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 70, position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, lineHeight: 1, fontWeight: 400 }}>
            <span style={{ color: '#e02020' }}>Chalk</span><span style={{ color: '#1a1a1a' }}>Walk's</span>
          </div>
          <div style={{ borderLeft: '1px solid #e8e4e0', paddingLeft: 16 }}>
            <div style={{ fontFamily: 'DM Serif Display', fontSize: 14, color: '#aaa', fontStyle: 'italic' }}>Design Studio</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={() => setShowEmailModal(true)} style={{ background: '#dc2626', border: 'none', borderRadius: 8, padding: '7px 18px', fontSize: 13, color: '#fff', cursor: 'pointer', fontWeight: 500 }}>
            📧 Send Proposal
          </button>
          <button onClick={handlePrint} style={{ background: '#1a1a1a', border: 'none', borderRadius: 8, padding: '7px 18px', fontSize: 13, color: '#fff', cursor: 'pointer', fontWeight: 500 }}>
            🖨 Print / Save PDF
          </button>
          <button onClick={onBack} style={{ background: '#f5f3f0', border: '1px solid #e8e4e0', borderRadius: 8, padding: '6px 16px', fontSize: 13, color: '#666', cursor: 'pointer' }}>
            ← Back
          </button>
        </div>
      </header>

      {/* Proposal Document */}
      <main style={{ maxWidth: 900, margin: '0 auto', padding: '3rem 2rem' }}>
        <div style={{ background: '#fff', borderRadius: 20, border: '1px solid #ede9e3', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          
          {/* Cover */}
          <div style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)', padding: '4rem 3rem', textAlign: 'center', color: '#fff' }}>
            <div style={{ fontSize: 12, letterSpacing: 4, textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>Exhibition Stall Proposal</div>
            <h1 style={{ fontFamily: 'DM Serif Display', fontSize: 42, margin: '0 0 16px', lineHeight: 1.2 }}>{entry.clientName || 'Unnamed Client'}</h1>
            <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>{entry.exhibition || '—'}</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>{entry.location || '—'} · {eventDateFormatted}</div>
            <div style={{ marginTop: 32, paddingTop: 32, borderTop: '1px solid rgba(255,255,255,0.15)', display: 'flex', justifyContent: 'center', gap: 40 }}>
              <div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>Project Number</div>
                <div style={{ fontSize: 20, fontWeight: 600 }}>#{projectNumber}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>Prepared On</div>
                <div style={{ fontSize: 20, fontWeight: 600 }}>{savedOnFormatted}</div>
              </div>
            </div>
          </div>

          {/* Project Overview */}
          <div style={{ padding: '3rem' }}>
            <div style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: '#aaa', marginBottom: 20 }}>Project Overview</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 40px', marginBottom: 40 }}>
              {[
                ['Client Name', entry.clientName],
                ['Exhibition Name', entry.exhibition],
                ['Location', entry.location],
                ['Exhibition Date', eventDateFormatted],
              ].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: value ? '#1a1a1a' : '#ccc' }}>{value || '—'}</div>
                </div>
              ))}
            </div>

            {/* Stall Design Section */}
            <div style={{ borderTop: '2px solid #f0ede8', paddingTop: 32, marginTop: 32 }}>
              <div style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: '#aaa', marginBottom: 20 }}>Recommended Stall Design</div>
              
              {/* Canvas Preview */}
              <div style={{ marginBottom: 32, borderRadius: 14, overflow: 'hidden', border: '1px solid #ede9e3' }}>
                <canvas ref={canvasRef} width={800} height={450} style={{ width: '100%', height: 'auto', display: 'block' }} />
              </div>

              {/* Stall Details */}
              <div style={{ background: '#faf9f7', borderRadius: 14, padding: '2rem', border: '1px solid #ede9e3' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                  <h2 style={{ fontFamily: 'DM Serif Display', fontSize: 28, color: '#1a1a1a', margin: 0 }}>{entry.stall.name}</h2>
                  <span style={{ fontSize: 13, background: entry.stall.color, color: entry.stall.accent, borderRadius: 20, padding: '6px 16px', fontWeight: 700, border: `2px solid ${entry.stall.accent}` }}>
                    {entry.stall.id}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 24 }}>
                  {[
                    ['Type', entry.stall.type],
                    ['Area', entry.stall.area + 'm²'],
                    ['Size', entry.stall.size],
                    ['Category', entry.stall.category],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', textTransform: 'capitalize' }}>{value}</div>
                    </div>
                  ))}
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8 }}>Description</div>
                  <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, margin: 0 }}>{entry.stall.desc}</p>
                </div>

                <div>
                  <div style={{ fontSize: 11, color: '#aaa', marginBottom: 10 }}>Features</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {entry.stall.tags.map((t) => (
                      <span key={t} style={{ fontSize: 12, background: entry.stall.color, color: entry.stall.accent, borderRadius: 8, padding: '5px 12px', fontWeight: 600, border: `1px solid ${entry.stall.accent}40` }}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Branding Elements */}
            {entry.logos?.length > 0 && (
              <div style={{ borderTop: '2px solid #f0ede8', paddingTop: 32, marginTop: 32 }}>
                <div style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: '#aaa', marginBottom: 20 }}>Branding Elements</div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {entry.logos.map((l, i) => (
                    <div key={i} style={{ width: 100, height: 100, border: '2px solid #ede9e3', borderRadius: 12, padding: 12, background: '#faf9f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={l.src} alt={`Logo ${i + 1}`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div style={{ borderTop: '2px solid #f0ede8', paddingTop: 32, marginTop: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#bbb', marginBottom: 8 }}>Prepared by</div>
              <div style={{ fontFamily: 'DM Serif Display', fontSize: 20, color: '#1a1a1a', marginBottom: 4 }}>ChalkWalk Consulting</div>
              <div style={{ fontSize: 13, color: '#888' }}>Design Studio</div>
              <div style={{ fontSize: 12, color: '#bbb', marginTop: 16 }}>noreply@chalkwalkconsulting.com</div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="no-print" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => { if (e.target === e.currentTarget) setShowEmailModal(false); }}>
          <div style={{ background: '#fff', borderRadius: 16, width: 'min(480px, 90vw)', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ background: '#1a1a1a', padding: '1.5rem 2rem', color: '#fff' }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Send Proposal to Client</h3>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', margin: '6px 0 0' }}>Enter the client's email address</p>
            </div>
            <div style={{ padding: '2rem' }}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 8, letterSpacing: 0.5 }}>Client Email Address</label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="client@example.com"
                  autoFocus
                  style={{ width: '100%', border: '1px solid #e5e1db', borderRadius: 10, padding: '12px 14px', fontSize: 14, color: '#1a1a1a', background: '#faf9f7', outline: 'none', boxSizing: 'border-box' }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSendProposal(); }}
                />
              </div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 20, lineHeight: 1.6 }}>
                The proposal will be sent from <strong>noreply@chalkwalkconsulting.com</strong> to the email address above.
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={handleSendProposal}
                  disabled={sendStatus === 'sending' || sendStatus === 'sent'}
                  style={{
                    flex: 1,
                    background: sendStatus === 'sent' ? '#16a34a' : sendStatus === 'error' ? '#dc2626' : '#dc2626',
                    border: 'none',
                    borderRadius: 10,
                    padding: '12px',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: sendStatus === 'sending' || sendStatus === 'sent' ? 'not-allowed' : 'pointer',
                  }}
                >
                  {sendStatus === 'sending' ? 'Sending…' : sendStatus === 'sent' ? '✓ Sent!' : sendStatus === 'error' ? 'Failed — Retry' : '📧 Send Proposal'}
                </button>
                <button
                  onClick={() => { setShowEmailModal(false); setClientEmail(''); setSendStatus(null); }}
                  disabled={sendStatus === 'sending'}
                  style={{
                    flex: 1,
                    background: '#f5f3f0',
                    border: '1px solid #e8e4e0',
                    borderRadius: 10,
                    padding: '12px',
                    color: '#666',
                    fontSize: 14,
                    cursor: sendStatus === 'sending' ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProposalPage;
